"use client";

import * as Dialog from "@radix-ui/react-dialog";
import Image from "next/image";
import { Play, X } from "lucide-react";
import { SectionHeading } from "@/components/ui/SectionHeading";
import { resolveMediaUrl } from "@/lib/media";
import type { ShortList } from "@/lib/dto";

type Props = {
  reels: ShortList[];
  title: string;
};

function ShortCard({ short }: { short: ShortList }) {
  const shortId = short.data.short_ids;
  const posterSrc = resolveMediaUrl(short.data.thumbnail?.url);
  const caption = short.title;

  if (!shortId) return null;

  return (
    <li className="w-48 shrink-0 snap-start sm:w-60">
      <Dialog.Root>
        <Dialog.Trigger
          type="button"
          aria-label={`Play ${caption}`}
          className="group relative block aspect-[4/3.5] w-full overflow-hidden rounded-lg bg-black"
        >
          {posterSrc ? (
            <Image
              src={posterSrc}
              alt={caption}
              fill
              sizes="250px"
              className="object-cover"
            />
          ) : null}
          <span className="absolute inset-0 bg-black/10 transition-colors group-hover:bg-black/20" />
          <span className="absolute inset-0 grid place-items-center">
            <span className="inline-grid h-11 w-11 place-items-center rounded-full bg-white/90 text-primary shadow-lg transition-transform group-hover:scale-105">
              <Play size={20} fill="currentColor" aria-hidden />
            </span>
          </span>
        </Dialog.Trigger>

        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 z-50 bg-black/85 backdrop-blur-sm data-[state=open]:animate-in data-[state=open]:fade-in" />
          <Dialog.Content className="fixed inset-0 z-50 grid place-items-center p-4">
            <Dialog.Title className="sr-only">{caption}</Dialog.Title>
            <Dialog.Description className="sr-only">
              YouTube Shorts video: {caption}
            </Dialog.Description>
            {/* Standard YouTube Shorts player size: 9:16, capped like
                YouTube's own popup so it doesn't stretch full-bleed. */}
            <div className="h-[min(80vh,711px)] w-[min(90vw,400px)] overflow-hidden rounded-xl bg-black shadow-2xl">
              <iframe
                src={`https://www.youtube-nocookie.com/embed/${shortId}?autoplay=1&playsinline=1`}
                title={caption}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="h-full w-full"
              />
            </div>
            <Dialog.Close
              aria-label="Close video"
              className="absolute top-4 right-4 inline-flex h-10 w-10 items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20"
            >
              <X size={18} aria-hidden />
            </Dialog.Close>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
      <p className="mt-2 px-3 line-clamp-2 text-md font-semibold text-headings">
        {caption}
      </p>
    </li>
  );
}

/** Horizontal rail of YouTube Shorts attached to a package. */
export function ReelsSection({ reels, title }: Props) {
  const playable = reels.filter((r) => r.data.short_ids);
  if (playable.length === 0) return null;

  return (
    <section id="reels" className="scroll-mt-32 mb-8">
      <SectionHeading title={title} />
      <ul className="mt-5 flex snap-x gap-4 overflow-x-auto pb-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        {playable.map((short) => (
          <ShortCard key={short.id} short={short} />
        ))}
      </ul>
    </section>
  );
}
